import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Building2, Truck, Siren, ChevronsUp, X, ArrowRight } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useGameStore } from '@/state/gameStore';

interface Step {
  icon: LucideIcon;
  title: string;
  body: string;
}

const STEPS: Step[] = [
  {
    icon: Building2,
    title: 'Wachen bauen',
    body:
      'Klick unten links auf den großen Dashboard-Button, wechsle in den Tab "Wachen" und baue deine erste Wache. Tipp: bestehende NPC-Wachen auf der Karte (durchsichtig mit "+") kannst du günstiger übernehmen.',
  },
  {
    icon: Truck,
    title: 'Fahrzeuge kaufen',
    body:
      'Öffne die Wache (Klick auf den Marker), wechsle auf den Tab "Fahrzeuge" und kaufe dort z.B. ein LF. Pro Wachen-Stufe stehen mehr Slots zur Verfügung.',
  },
  {
    icon: Siren,
    title: 'Notrufe abarbeiten',
    body:
      'Sobald eine fertige Wache existiert, spawnen Einsätze. Klick auf den Marker, wähle Fahrzeuge im Disposition-Panel und alarmiere. Anforderungen werden NICHT vorab angezeigt — entscheide aus Erfahrung.',
  },
  {
    icon: ChevronsUp,
    title: 'Level-Up = größeres Einsatzgebiet',
    body:
      'Klick auf die Level-Pille oben rechts. Mit höherem Level wächst dein Einsatzgebiet — und damit die Notruf-Frequenz. Viel Erfolg.',
  },
];

/**
 * Phase 7.4.6 — First-run onboarding. 4-step overlay, dismissible at any
 * point. Sets `uiPrefs.onboardingCompleted` when finished or skipped.
 */
export function OnboardingOverlay() {
  const completed = useGameStore((s) => s.uiPrefs.onboardingCompleted);
  const completeOnboarding = useGameStore((s) => s.completeOnboarding);
  const homeCity = useGameStore((s) => s.homeCity);
  const reduceMotion = useGameStore((s) => s.uiPrefs.reduceMotion);
  const [step, setStep] = useState(0);

  // Don't show before city is picked, and don't show if already completed.
  if (completed || !homeCity) return null;

  const current = STEPS[step];
  const Icon = current.icon;
  const isLast = step === STEPS.length - 1;
  const dur = reduceMotion ? 0.08 : 0.2;

  const finish = () => completeOnboarding();
  const next = () => (isLast ? finish() : setStep((s) => s + 1));

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[120] flex items-center justify-center px-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: dur }}
      >
        <div className="absolute inset-0 bg-bg-overlay backdrop-blur-md" aria-hidden />
        <motion.div
          role="dialog"
          aria-modal="true"
          aria-labelledby="onboarding-title"
          className="relative w-full max-w-md rounded-lg border border-border-strong bg-bg-secondary p-6 shadow-2xl"
          initial={{ opacity: 0, y: 12, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.96 }}
          transition={{ duration: dur }}
        >
          <button
            type="button"
            onClick={finish}
            aria-label="Tutorial überspringen"
            className="absolute right-3 top-3 text-fg-muted transition hover:text-fg-primary"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>

          <div className="flex items-start gap-3">
            <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-md bg-accent-emergency/15 text-accent-emergency">
              <Icon className="h-5 w-5" aria-hidden />
            </span>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-accent-emergency">
                Schritt {step + 1} / {STEPS.length}
              </p>
              <h3 id="onboarding-title" className="font-display text-lg font-bold">
                {current.title}
              </h3>
            </div>
          </div>

          <p className="mt-4 text-sm leading-relaxed text-fg-secondary">{current.body}</p>

          <div className="mt-5 flex items-center gap-1">
            {STEPS.map((_, i) => (
              <span
                key={i}
                className={[
                  'h-1.5 flex-1 rounded-full transition',
                  i <= step ? 'bg-accent-emergency' : 'bg-bg-elevated',
                ].join(' ')}
              />
            ))}
          </div>

          <div className="mt-5 flex justify-between gap-2">
            <button
              type="button"
              onClick={finish}
              className="rounded-md border border-border-strong bg-bg-elevated px-4 py-2 text-sm font-semibold text-fg-secondary transition hover:bg-bg-elevated/70"
            >
              Überspringen
            </button>
            <button
              type="button"
              onClick={next}
              autoFocus
              className="inline-flex items-center gap-1.5 rounded-md bg-accent-emergency px-4 py-2 text-sm font-semibold text-white transition hover:bg-accent-emergency-hover"
            >
              {isLast ? 'Los geht\'s' : 'Weiter'}
              {!isLast && <ArrowRight className="h-3.5 w-3.5" aria-hidden />}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
